import { useEffect, useMemo, useRef, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Streamdown } from "streamdown";
import { AgentQuestionOptions } from "@/components/ui/agent-question-options";
import { DeletionConfirm } from "@/components/ui/deletion-confirm";
import { TypingIndicator } from "@/components/ui/typing-indicator";
import { answerableCreativeCards, CreativeCardFor } from "@/components/workspace/creative-card";
import { MonitoringReviewBar } from "@/components/workspace/monitoring-review-bar";
import { WorkspaceChatComposer } from "@/components/workspace/workspace-chat-composer";
import { WorkspacePanelHeading } from "@/components/workspace/workspace-panel-heading";
import type { AgentQuestion, AgentQuestionOption } from "@/hooks/use-agent-questions";
import { useTranslations } from "@/hooks/use-translations";
import type { ChatHistoryMessage } from "@/lib/api";

interface WorkspaceChatProps {
	messages: ChatHistoryMessage[];
	isLoading: boolean;
	isSending: boolean;
	/** Choices of the agent's live question, if it asked one. */
	questionOptions: AgentQuestionOption[] | undefined;
	onOptionSelect: (label: string) => void;
	/** A deletion waiting for Delete or Cancel; the composer is blocked meanwhile (M-05). */
	pendingDeletion?: AgentQuestion | null;
	onDeletionChoice: (choice: "delete" | "cancel") => void;
	isAnsweringDeletion?: boolean;
	/** The chat was opened from a monitoring notification. */
	showMonitoringNotice: boolean;
	/** The proposal under discussion; its Approve/Reject buttons live in the chat. */
	monitoringReviewId?: string;
	onDismissMonitoring: () => void;
	input: string;
	onInputChange: (value: string) => void;
	onSubmit: (e: FormEvent) => void;
}

/** Center column: message thread, agent choices and the composer. */
export function WorkspaceChat({
	messages,
	isLoading,
	isSending,
	questionOptions,
	onOptionSelect,
	pendingDeletion,
	onDeletionChoice,
	isAnsweringDeletion,
	showMonitoringNotice,
	monitoringReviewId,
	onDismissMonitoring,
	input,
	onInputChange,
	onSubmit,
}: WorkspaceChatProps) {
	const t = useTranslations("workspace");
	const { i18n } = useTranslation();
	const chatEndRef = useRef<HTMLDivElement>(null);
	const answerable = useMemo(() => answerableCreativeCards(messages), [messages]);

	// Scroll chat on new messages
	useEffect(() => {
		chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages, isSending]);

	return (
		<motion.main
			initial={{ opacity: 0, y: 10 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.3, delay: 0.1 }}
			className="flex-1 flex flex-col rounded-none border border-t-0 border-border-subtle bg-bg-surface min-w-0"
		>
			{/* Chat Label */}
			<WorkspacePanelHeading title={t("chat")} />

			{/* Messages */}
			<div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-2 scrollbar-thin">
				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<Loader2 size={24} className="animate-spin text-text-tertiary" />
					</div>
				) : messages.length === 0 ? (
					<div className="flex items-center justify-center py-12">
						<p className="text-sm text-text-tertiary">
							No messages yet. Start a conversation!
						</p>
					</div>
				) : (
					<div className="space-y-4">
						<AnimatePresence initial={false}>
							{messages.map((msg) => (
								<motion.div
									key={msg.id}
									initial={{ opacity: 0, y: 10, scale: 0.95 }}
									animate={{ opacity: 1, y: 0, scale: 1 }}
									transition={{ duration: 0.2 }}
									className="mb-4"
								>
									{msg.role === "assistant" ? (
										<div>
											<div className="text-sm text-text-primary leading-relaxed chat-markdown chat-message-markdown">
												<Streamdown>{msg.content || ""}</Streamdown>
											</div>
											<CreativeCardFor message={msg} answerable={answerable} />
										</div>
									) : (
										<div className="flex justify-end">
											<div className="max-w-[80%] px-4 py-3 rounded-2xl bg-brand-100 text-text-primary text-sm break-words shadow-sm">
												<Streamdown>{msg.content || ""}</Streamdown>
											</div>
										</div>
									)}
								</motion.div>
							))}
						</AnimatePresence>

						{isSending && (
							<motion.div
								initial={{ opacity: 0, y: 5 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0 }}
								className="mb-4"
							>
								<TypingIndicator />
							</motion.div>
						)}
					</div>
				)}
				<div ref={chatEndRef} />
			</div>

			{/* A deletion: only Delete or Cancel answers it */}
			{pendingDeletion ? (
				<div className="px-6">
					<DeletionConfirm
						question={pendingDeletion}
						onChoose={onDeletionChoice}
						disabled={isAnsweringDeletion}
					/>
				</div>
			) : questionOptions?.length ? (
				<div className="px-6">
					<AgentQuestionOptions
						options={questionOptions}
						onSelect={onOptionSelect}
						disabled={isSending}
					/>
				</div>
			) : null}

			{/* Chat Input */}
			{monitoringReviewId ? (
				<MonitoringReviewBar reviewId={monitoringReviewId} onDismiss={onDismissMonitoring} />
			) : showMonitoringNotice && (
				<div role="status" className="px-6 pb-2 text-sm text-info">
					{i18n.language.startsWith("ru")
						? "Обсуждаем запрос из уведомления. Изменения требуют явного ответа."
						: "Discussing the request from your notification. Changes require an explicit answer."}{" "}
					<button
						className="text-text-link underline underline-offset-2"
						onClick={onDismissMonitoring}
					>
						{i18n.language.startsWith("ru") ? "Закрыть" : "Close"}
					</button>
				</div>
			)}
			<WorkspaceChatComposer
				value={input}
				onChange={onInputChange}
				onSubmit={onSubmit}
				placeholder={pendingDeletion ? t("deletion.blocked") : t("askAnything")}
				disabled={!!pendingDeletion}
			/>
		</motion.main>
	);
}
